import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, apiFetch, type RequestOptions } from '../api/client';
import { identityFetch } from '../api/identity';

/**
 * Nạp dữ liệu từ delivery-api.
 *
 * Tự viết thay vì kéo TanStack Query: web-admin không cần cache xuyên màn
 * hình, không cần optimistic update, không cần đồng bộ nhiều tab. Thứ nó cần
 * là ba trạng thái (đang tải / lỗi / dữ liệu) và huỷ request cũ khi tham số
 * đổi — vừa đúng những gì file này làm.
 */
export interface ApiState<T> {
  data: T | undefined;
  loading: boolean;
  error: ApiError | undefined;
  reload: () => void;
}

/**
 * `options.identity` chuyển lời gọi sang service identity.
 *
 * Hai service dùng chung access token nên chỉ khác base URL. Thêm một cờ ở đây
 * rẻ hơn nhiều so với nhân bản cả hook chỉ để đổi một chuỗi.
 */
export interface UseApiOptions extends Omit<RequestOptions, 'signal'> {
  identity?: boolean;
}

export function useApi<T>(path: string, options: UseApiOptions = {}, enabled = true): ApiState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [tick, setTick] = useState(0);

  // Tham số đi qua ref rồi mới vào effect: `options` là object literal mới ở
  // mỗi lần render, đưa thẳng vào deps sẽ tạo vòng lặp nạp vô tận. Chuỗi hoá
  // để so sánh GIÁ TRỊ chứ không so sánh tham chiếu.
  const optionsKey = JSON.stringify(options);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(undefined);

    const { identity, ...requestOptions } = optionsRef.current;
    const request = identity ? identityFetch<T> : apiFetch<T>;

    request(path, { ...requestOptions, signal: controller.signal })
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        // Huỷ do tham số đổi không phải lỗi — hiện thông báo đỏ ở đây sẽ làm
        // màn hình nhấp nháy lỗi mỗi lần người dùng gõ vào ô tìm kiếm.
        if (controller.signal.aborted) return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'api.error.internal'));
        setLoading(false);
      });

    return () => controller.abort();
    // optionsKey thay cho options: xem chú thích ở trên.
  }, [path, optionsKey, enabled, tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);

  return { data, loading, error, reload };
}

/**
 * Gọi API thay đổi dữ liệu (POST/PUT/DELETE).
 *
 * Tách khỏi `useApi` vì vòng đời khác hẳn: mutation chạy khi người dùng bấm,
 * không chạy khi render, và không được tự huỷ khi component re-render — nửa
 * chừng một lệnh ghi mà bị huỷ là cách tạo dữ liệu dở dang.
 */
export function useMutation<TInput, TResult>(
  request: (input: TInput) => Promise<TResult>,
): {
  run: (input: TInput) => Promise<TResult | undefined>;
  pending: boolean;
  error: ApiError | undefined;
  reset: () => void;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);

  const run = useCallback(
    async (input: TInput): Promise<TResult | undefined> => {
      setPending(true);
      setError(undefined);
      try {
        return await request(input);
      } catch (cause) {
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'api.error.internal'));
        return undefined;
      } finally {
        setPending(false);
      }
    },
    [request],
  );

  const reset = useCallback(() => setError(undefined), []);

  return { run, pending, error, reset };
}

/**
 * Hoãn cập nhật giá trị cho tới khi người dùng ngừng gõ.
 *
 * Ô tìm kiếm không debounce nghĩa là mỗi phím một request; gõ "Toán học" bắn
 * 8 request mà chỉ request cuối có ý nghĩa.
 */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
